"use client";

import { useState, useCallback, useMemo } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input, Textarea } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Icon } from "@/components/ui/icon";
import { PICKER_COLORS } from "./types";

export function StageCreatePill({
  usedColors,
  onAdd,
}: {
  usedColors: Set<string>;
  onAdd: (name: string, color: string, description: string) => void;
}) {
  const [isCreating, setIsCreating] = useState(false);
  const nextColor = useMemo(
    () => PICKER_COLORS.find((c) => !usedColors.has(c)) ?? PICKER_COLORS[0],
    [usedColors],
  );
  const [name, setName] = useState("");
  const [color, setColor] = useState(nextColor);
  const [description, setDescription] = useState("");

  const handleCreate = useCallback(() => {
    onAdd(name.trim() || "Untitled Stage", color, description);
    setName("");
    setColor(PICKER_COLORS.find((c) => !usedColors.has(c)) ?? PICKER_COLORS[0]);
    setDescription("");
    setIsCreating(false);
  }, [name, color, description, onAdd, usedColors]);

  const handleCancel = useCallback(() => {
    setName("");
    setColor(nextColor);
    setDescription("");
    setIsCreating(false);
  }, [nextColor]);

  return (
    <motion.div
      className="bg-surface-header border border-border flex h-auto shrink-0 overflow-hidden shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)] relative z-[1] group"
      variants={{
        closed: { width: "20rem", borderRadius: "10rem" },
        open: { width: "100%", borderRadius: "0.5rem" },
      }}
      initial="closed"
      animate={isCreating ? "open" : "closed"}
      transition={{ duration: 0.5 }}
    >
      {!isCreating && (
        <span className="absolute inset-0 rounded-[inherit] bg-contrast/4 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" />
      )}
      <AnimatePresence mode="wait" initial={false}>
        {!isCreating ? (
          <motion.button
            key="pill-button"
            type="button"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
            className="flex h-8 w-full items-center justify-center gap-1.5 px-2 text-center cursor-pointer"
            onClick={() => setIsCreating(true)}
          >
            <Icon name="IconPlusSmall" size={16} className="text-heading" />
            <span className="text-sm text-heading leading-5 whitespace-nowrap">
              Add a stage for tracking pipeline{" "}
              <span className="text-contrast/30">(optional)</span>
            </span>
          </motion.button>
        ) : (
          <motion.div
            key="create-form"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
            className="w-full p-4 overflow-hidden"
          >
            <div className="flex flex-col gap-3">
              <div>
                <Label className="text-[13px] text-contrast/50 mb-1.5 block">
                  Stage Label
                </Label>
                <Input
                  type="text"
                  variant="default"
                  size="sm"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="First Round Interview"
                  maxLength={50}
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === "Escape") handleCancel();
                  }}
                />
              </div>

              <div>
                <Label className="text-[13px] text-contrast/50 mb-1.5 block">
                  Color
                </Label>
                <div className="flex items-center gap-2">
                  {PICKER_COLORS.map((c) => (
                    <button
                      key={c}
                      type="button"
                      onClick={() => setColor(c)}
                      className={`size-4 rounded-full cursor-pointer transition-shadow shrink-0 ${
                        color === c
                          ? "shadow-[0_0_0_2px_var(--surface-header),0_0_0_3.5px_rgba(0,0,0,0.25)] dark:shadow-[0_0_0_2px_var(--surface-header),0_0_0_3.5px_rgba(255,255,255,0.5)]"
                          : "hover:shadow-[0_0_0_2px_var(--surface-header),0_0_0_3.5px_rgba(0,0,0,0.12)] dark:hover:shadow-[0_0_0_2px_var(--surface-header),0_0_0_3.5px_rgba(255,255,255,0.2)]"
                      }`}
                      style={{ backgroundColor: c }}
                    />
                  ))}
                </div>
              </div>

              <div>
                <Label className="text-[13px] text-contrast/50 mb-1.5 block">
                  Stage Description
                </Label>
                <Textarea
                  variant="default"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="This stage is for all Profiles who get advanced to the first round interview."
                  rows={4}
                />
              </div>

              <div className="flex items-center justify-end gap-3">
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={handleCancel}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  variant="primary"
                  size="sm"
                  onClick={handleCreate}
                >
                  Create
                </Button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
